import Link from "next/link";
import Image from "next/image";
import { MapPin, Home, Building, Banknote, ArrowRight } from "lucide-react";
import type { JobCard } from "@/lib/types";
import { EMPLOYMENT_TYPE_LABEL, WORK_STYLE_LABEL } from "@/lib/types";

type Props = {
  job: JobCard;
  className?: string;
};

/** 働き方に応じた lucide アイコン */
const WORK_STYLE_ICON = {
  onsite: Building,
  remote: Home,
  hybrid: MapPin,
} as const;

export default function JobCardItem({ job, className }: Props) {
  const href = `/jobs/${job.slug}`;
  const WorkIcon = WORK_STYLE_ICON[job.work_style];

  return (
    <article
      className={`flex h-full flex-col overflow-hidden rounded-xl2 border border-line bg-white shadow-card transition-shadow hover:shadow-cardHover ${className ?? ""}`}
    >
      {/* カバー画像（任意） */}
      {job.cover_image && (
        <Link
          href={href}
          className="relative block aspect-[16/9] overflow-hidden bg-mist"
          aria-hidden
          tabIndex={-1}
        >
          <Image
            src={job.cover_image}
            alt=""
            fill
            sizes="(min-width: 1024px) 360px, (min-width: 640px) 50vw, 100vw"
            className="object-cover transition-transform duration-500 hover:scale-105"
          />
        </Link>
      )}

      <div className="flex flex-1 flex-col p-7">
      {/* 雇用形態バッジ */}
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center rounded-full bg-navy-50 px-3 py-1 font-en text-[11px] font-semibold uppercase tracking-[0.08em] text-navy-700">
          {EMPLOYMENT_TYPE_LABEL[job.employment_type]}
        </span>
        {job.department && (
          <span className="truncate text-xs font-medium text-muted">{job.department}</span>
        )}
      </div>

      {/* タイトル */}
      <h3 className="mt-4 text-lg font-bold leading-snug text-navy-900">
        <Link href={href} className="transition-colors hover:text-navy-700">
          {job.title}
        </Link>
      </h3>

      {/* 概要 2行省略 */}
      <p className="mt-3 line-clamp-2 text-sm leading-relaxed text-muted">{job.summary}</p>

      {/* メタ情報 */}
      <dl className="mt-5 space-y-2 border-t border-line pt-5 text-xs text-muted">
        {job.location && (
          <div className="flex items-center gap-2">
            <MapPin className="h-3.5 w-3.5 shrink-0 text-navy-300" aria-hidden />
            <dt className="sr-only">勤務地</dt>
            <dd>{job.location}</dd>
          </div>
        )}
        <div className="flex items-center gap-2">
          <WorkIcon className="h-3.5 w-3.5 shrink-0 text-navy-300" aria-hidden />
          <dt className="sr-only">働き方</dt>
          <dd>{WORK_STYLE_LABEL[job.work_style]}</dd>
        </div>
        {job.salary_text && (
          <div className="flex items-center gap-2">
            <Banknote className="h-3.5 w-3.5 shrink-0 text-navy-300" aria-hidden />
            <dt className="sr-only">給与</dt>
            <dd>{job.salary_text}</dd>
          </div>
        )}
      </dl>

      <Link
        href={href}
        className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-navy-700 transition-colors hover:text-navy-900"
      >
        詳細を見る
        <ArrowRight className="h-4 w-4" aria-hidden />
      </Link>
      </div>
    </article>
  );
}
